/**
 * DemoLockedPage — shown in place of locked pages during demo mode.
 *
 * Renders a centred card with icon, description, and CTA to connect
 * a real property. Locked pages: Places, Progress, House Rules, Display, Settings.
 */

import type { LucideIcon } from "lucide-react"
import { Lock, ArrowRight } from "lucide-react"
import { Link } from "react-router"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"

interface DemoLockedPageProps {
  title: string
  description: string
  previewHint: string
  icon?: LucideIcon
}

export function DemoLockedPage({
  title,
  description,
  previewHint,
  icon: Icon = Lock,
}: DemoLockedPageProps) {
  return (
    <div className="flex items-center justify-center min-h-[60vh] px-8">
      <Card className="max-w-lg w-full text-center">
        <CardContent className="pt-10 pb-8 px-8 space-y-4">
          <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center">
            <Icon className="h-6 w-6 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">{title}</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {description}
          </p>
          <p className="text-xs text-muted-foreground/70 italic">
            {previewHint}
          </p>
          <div className="pt-2 space-y-3">
            <Button asChild>
              <Link to="/onboarding">
                Connect Your Property
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
            <div>
              <Link
                to="../"
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                Explore demo pages
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
